import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  BarChart as BarChartIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SurveyCreator from '../components/admin/SurveyCreator';
import SurveyResponses from '../components/admin/SurveyResponses';
import { API_URL } from '../config/api';

const AdminSurveyManagement = () => {
  const { getAuthHeaders } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [viewResponses, setViewResponses] = useState(false);
  const [selectedSurveyForResponses, setSelectedSurveyForResponses] = useState(null);
  const [surveyData, setSurveyData] = useState({
    title: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API_URL}/surveys/surveys`, {
        headers: getAuthHeaders()
      });
      setSurveys(response.data.surveys);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to fetch surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSurvey = () => {
    setDialogMode('create');
    setSelectedSurvey(null);
    setSurveyData({
      title: '',
      description: '',
      isActive: true,
      module: 'feedback'
    });
    setOpenDialog(true);
  };

  const handleEditSurvey = (survey) => {
    setDialogMode('edit');
    setSelectedSurvey(survey);
    setSurveyData({
      title: survey.title,
      description: survey.description,
      isActive: survey.isActive,
      module: survey.module || 'general'
    });
    setOpenDialog(true);
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;

    try {
      await axios.delete(`${API_URL}/surveys/surveys/${surveyId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Survey deleted successfully');
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error('Failed to delete survey');
    }
  };

  const handleSaveSurvey = async (surveyJson) => {
    try {
      const payload = {
        ...surveyData,
        module: surveyJson.module,
        questions: surveyJson.pages[0].elements
      };

      if (dialogMode === 'create') {
        await axios.post(`${API_URL}/surveys/surveys`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Survey created successfully');
      } else {
        await axios.put(`${API_URL}/surveys/surveys/${selectedSurvey._id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Survey updated successfully');
      }

      setOpenDialog(false);
      fetchSurveys();
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey');
    }
  };

  const handleToggleActive = async (surveyId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/surveys/surveys/${surveyId}`, {
        isActive: !currentStatus
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Survey status updated');
      fetchSurveys();
    } catch (error) {
      console.error('Error updating survey status:', error);
      toast.error('Failed to update survey status');
    }
  };

  const handleViewResponses = (survey) => {
    setSelectedSurveyForResponses(survey);
    setViewResponses(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ mb: 4, mt: 2 }}>
        <Typography sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1, fontSize: '30px', mb: 1 }}>
          Survey Management
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
          <Typography variant="h5" color="text.secondary" sx={{ fontStyle: 'italic', fontWeight: 400, fontSize: '20px' }}>
            Create, manage, and analyze employee surveys
          </Typography>
          <Button
            variant="contained"
            onClick={handleCreateSurvey}
            sx={{
              py: 1.2,
              px: 3.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: 15,
            }}
          >
            Create New Survey
          </Button>
        </Box>
      </Box>

      {surveys.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '20px' }}>
                No surveys created yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first survey to get started
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Survey Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Questions</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {surveys.map((survey) => (
                <TableRow key={survey._id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                  <TableCell>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {survey.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {survey.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={survey.module || 'feedback'} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {survey.questions?.length || 0} questions
                      </Typography>
                      {(() => {
                        const sections = new Set(survey.questions?.map(q => q.section || 'General') || []);
                        if (sections.size > 1) {
                          return (
                            <Typography variant="caption" color="text.secondary">
                              {sections.size} sections
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        label={survey.isActive ? 'Active' : 'Inactive'} 
                        size="small" 
                        color={survey.isActive ? 'success' : 'default'} 
                        variant="outlined"
                        sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                      />
                      <Tooltip title={survey.isActive ? 'Deactivate' : 'Activate'}>
                        <Switch
                          checked={survey.isActive}
                          onChange={() => handleToggleActive(survey._id, survey.isActive)}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#7e57c2',
                              '&:hover': {
                                backgroundColor: 'rgba(126, 87, 194, 0.08)',
                              },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#7e57c2',
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: '#ccc',
                            },
                            '& .MuiSwitch-thumb': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {survey.createdBy?.firstName} {survey.createdBy?.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Edit Survey">
                        <IconButton
                          size="small"
                          onClick={() => handleEditSurvey(survey)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Responses">
                        <IconButton
                          size="small"
                          onClick={() => handleViewResponses(survey)}
                          color="info"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Survey">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSurvey(survey._id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Survey' : 'Edit Survey'}
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <TextField
              fullWidth
              label="Survey Title"
              value={surveyData.title}
              onChange={(e) => setSurveyData({ ...surveyData, title: e.target.value })}
              margin="normal"
              required
              placeholder="Enter a descriptive title for your survey"
            />
            <TextField
              fullWidth
              label="Description"
              value={surveyData.description}
              onChange={(e) => setSurveyData({ ...surveyData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={surveyData.isActive}
                  onChange={(e) => setSurveyData({ ...surveyData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
          
          <SurveyCreator
            surveyData={selectedSurvey}
            onSave={handleSaveSurvey}
            mode={dialogMode}
            surveyTitle={surveyData.title}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Survey Responses Dialog */}
      <Dialog
        open={viewResponses}
        onClose={() => setViewResponses(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Survey Responses - {selectedSurveyForResponses?.title}
        </DialogTitle>
        <DialogContent>
          {selectedSurveyForResponses && (
            <SurveyResponses surveyId={selectedSurveyForResponses._id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewResponses(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSurveyManagement; 